import React, { useState, useEffect } from 'react';
import Sidebar from './Layout/sidebar';
import Header from './Layout/header';
import SecondaryMenu from './Layout/secondaryMenu';
import Router from './router';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { initiateSocketConnection, disconnectSocket } from './Socket/socket';
import { NotificationProvider } from './Context/NotificationContext';
import { MessageProvider } from './Context/MessageContext';
import { ToastProvider, useToast } from './Context/ToastContext';
import { ConfirmProvider } from './Context/ConfirmContext';
import { CapabilityProvider } from './Context/CapabilityContext';
import ExpertTermsBanner from './Components/ExpertTermsBanner';
import PendingInviteClaimer from './Components/PendingInviteClaimer';
import { jwtDecode } from 'jwt-decode'
import { isTeamMemberAuthenticated, getTeamUser, getTeamToken } from './services/teamAuthService'

// Component to handle session expiration events and show toast notifications
const SessionExpirationHandler = () => {
  const { error } = useToast();

  useEffect(() => {
    const handleSessionExpired = (event) => {
      const { message } = event.detail;
      error(message || 'Your session has expired. Please log in again.', {
        title: 'Session Expired',
        duration: 5000
      });
    };

    window.addEventListener('session-expired', handleSessionExpired);
    
    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, [error]);

  return null;
};

// Handle "out of AI tokens" (HTTP 402) events dispatched by the axios interceptors.
// If the logged-in user is the one who's out of tokens, send them to the purchase
// screen. If a subcontractor is operating a shared case whose OWNER is out of tokens,
// they can't buy for the owner — just inform them (billing resolves to the owner).
const InsufficientTokensHandler = () => {
  const { error, warning } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    let lastShownAt = 0;
    const handleInsufficientTokens = (event) => {
      // De-dupe: a burst of failing AI calls can fire several 402s at once.
      const now = Date.now();
      if (now - lastShownAt < 4000) return;
      lastShownAt = now;

      const detail = event.detail || {};
      const operatorIsBilled = detail.operatorIsBilled !== false; // default: it's my own balance
      const purchaseUrl = detail.purchaseUrl || '/settings/tokens';

      if (operatorIsBilled) {
        error(
          detail.message || "You're out of AI tokens. Purchase more to continue using AI features.",
          {
            title: 'Out of AI tokens',
            duration: 8000,
            action: { label: 'Buy tokens', onClick: () => navigate(purchaseUrl) },
          }
        );
        // Also route them there so the purchase screen is front-and-center.
        navigate(purchaseUrl);
      } else {
        // Shared case: the case owner (contractor) is out of tokens — inform only.
        (warning || error)(
          detail.message || 'The case owner is out of AI tokens. AI features are paused until they top up.',
          { title: 'Case owner out of AI tokens', duration: 8000 }
        );
      }
    };

    window.addEventListener('insufficient-tokens', handleInsufficientTokens);
    return () => window.removeEventListener('insufficient-tokens', handleInsufficientTokens);
  }, [error, warning, navigate]);

  return null;
};

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  height: 94%;
  overflow-y: auto;
  overflow-x: hidden;
`;

const RouterContent = styled.div`
  width: 93%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background: linear-gradient(180deg, #0f1419 0%, #1a1f2e 100%);
`;

function App() {
  const [expanded, setExpanded] = useState(false);
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const [allowResizing, setAllowResizing] = useState(false);
  const [allowDragging, setAllowDragging] = useState(false);
  const [allowFloating, setAllowFloating] = useState(false);
  const skipSocket = new URLSearchParams(window.location.search).get('skipSocket') === '1';
  const location = useLocation();
  
  // Check if we're on the workspace/main page (both routes lead to workspace)
  const isWorkspace = location.pathname === '/main' || 
                      location.pathname.startsWith('/main/') ||
                      location.pathname === '/workspace' ||
                      location.pathname.startsWith('/workspace/');
  
  // Check if we're on a standalone page that shouldn't have the app layout
  const isStandalonePage = location.pathname.startsWith('/fill-form/') ||
                           location.pathname.startsWith('/service-questionnaire/') ||
                           location.pathname.startsWith('/referral-consent/') ||
                           location.pathname === '/login' ||
                           location.pathname === '/accept-invite' ||
                           location.pathname === '/reset-password' ||
                           location.pathname === '/member-login';

  const toggleFlag = () => {
    setExpanded((expanded) => !expanded);
  };

  const handleToggleControl = (control) => {
    switch (control) {
      case 'resize':
        setAllowResizing(!allowResizing);
        break;
      case 'drag':
        setAllowDragging(!allowDragging);
        break;
      case 'float':
        setAllowFloating(!allowFloating);
        break;
      default:
        console.warn('Unknown control type:', control);
    }
  };

  // Check if team member is authenticated
  const isTeamAuth = isTeamMemberAuthenticated();
  const teamUser = getTeamUser();
  
  // Combined auth check
  const isFullyAuthenticated = isAuthenticated || isTeamAuth;

  useEffect(() => {
    // Function to connect to the socket server when authenticated
    const connectSocket = async () => {
      if (skipSocket) return;
      
      // Handle Auth0 authenticated users (experts)
      if (isAuthenticated && user) {
        try {
          // Get the user's access token
          const token = await getAccessTokenSilently();
          const decodedToken = jwtDecode(token);
          console.log(decodedToken.preferred_language)
          const data = {};
          const response = await fetch(`${process.env.REACT_APP_SERVER}calander/setNewCalander`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(data),
          });

          // Get user information from the Auth0 context
          const userData = {
            userId: user.sub, // Auth0 user ID (unique identifier)
            username: user.name, // User's display name
            email: user.email, // User's email address
            preferred_language: decodedToken.preferred_language, // User's preferred language
            userType: 'expert',
            expertId: user.sub,
            token: token // Optionally pass the token for additional security
          };

          console.log('🔌 Initializing socket connection in App (Expert)');
          initiateSocketConnection(userData);
        } catch (error) {
          console.error('❌ Error setting up socket in App:', error);
        }
      }
      // Handle team member authenticated users
      else if (isTeamAuth && teamUser) {
        try {
          const token = getTeamToken();
          
          // Set up calendar for team member
          await fetch(`${process.env.REACT_APP_SERVER}calander/setNewCalander`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({}),
          });

          // Get user information from team auth
          const userData = {
            userId: teamUser.id,
            username: teamUser.name,
            email: teamUser.email,
            preferred_language: 'en',
            userType: 'teamMember',
            expertId: teamUser.expertId,
            memberId: teamUser.id,
            role: teamUser.role,
            token: token
          };

          console.log('🔌 Initializing socket connection in App (Team Member)');
          initiateSocketConnection(userData);
        } catch (error) {
          console.error('❌ Error setting up socket for team member:', error);
        }
      }
    };

    // Call the function to connect if authenticated
    connectSocket();
    // Disconnect the socket when the component unmounts or when not authenticated
    return () => {
      if (isFullyAuthenticated && !skipSocket) {
        disconnectSocket();
      }
    };
  }, [isAuthenticated, isTeamAuth, getAccessTokenSilently, user, teamUser, skipSocket, isFullyAuthenticated]);

  // For standalone pages (like form filling), render without the app layout
  if (isStandalonePage) {
    return (
      <Router
        allowResizing={allowResizing}
        allowDragging={allowDragging}
        allowFloating={allowFloating}
      />
    );
  }

  return (
    <ConfirmProvider>
    <ToastProvider>
      <SessionExpirationHandler />
      <InsufficientTokensHandler />
      {/* Existing experts accept the current Expert Terms once (non-blocking, floating card). */}
      {isAuthenticated && !isTeamAuth && <ExpertTermsBanner />}
      {/* An adviser-network invitation opened before sign-up: attach it to the new account. */}
      {isAuthenticated && !isTeamAuth && <PendingInviteClaimer />}
      <NotificationProvider>
        <MessageProvider>
          <CapabilityProvider>
          <AppContainer>
          <Header
            onExpandToggle={toggleFlag}
            expanded={expanded}
            allowResizing={allowResizing}
            allowDragging={allowDragging}
            allowFloating={allowFloating}
            onToggleControl={handleToggleControl}
            isWorkspace={isWorkspace}
          />
          <MainContent>
            <Sidebar expanded={expanded} onExpandToggle={toggleFlag} isWorkspace={isWorkspace} />
            <RouterContent $isWorkspace={isWorkspace}>
              <Router
                allowResizing={allowResizing}
                allowDragging={allowDragging}
                allowFloating={allowFloating}
              />
            </RouterContent>
            <SecondaryMenu isWorkspace={isWorkspace} />
          </MainContent>
          <div style={{ background: "linear-gradient(90deg, #0f1419 0%, #1a1f2e 50%, #0f1419 100%)", height: '1%', width: "100%" }} />
          </AppContainer>
          </CapabilityProvider>
        </MessageProvider>
      </NotificationProvider>
    </ToastProvider>
    </ConfirmProvider>
  );
}

export default App;
